import { createAsyncThunk } from '@reduxjs/toolkit'
import {
    dashboardFiltersRequest,
    dashboardDataRequest,
    dashboardPanelsRequest,
    dashboardDateFilterRequest,
} from '../../apiClient/dashboardRequests';
import { buildPanelRequestKey, getPanelKey } from './panelRequestUtils';

let dashboardPeriodicCronIntervalId = null;

const getErrorDetail = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') return JSON.stringify(detail);
    return err?.message || 'No se ha podido cargar el panel';
};

const getRequestId = (err) => {
    const headers = err?.response?.headers;
    return err?.response?.data?.request_id ||
        headers?.['x-request-id'] ||
        headers?.['X-Request-ID'] ||
        headers?.get?.('x-request-id') ||
        null;
};

export const getDashboardRequestError = (err) => ({
    http_status_code:
        err?.response?.status ||
        (err?.code === 'ERR_NETWORK' || err?.message?.includes('ERR_CONNECTION_REFUSED') ? 503 : 500),
    detail: getErrorDetail(err),
    request_id: getRequestId(err),
});

const selectDashboardPanelsByType = (state, dashboardId, panelTypes) => {
    const dashboard = state?.dashboard?.dashboards?.[dashboardId];
    const panels = dashboard?.panels || {};
    const types = Array.isArray(panelTypes) ? panelTypes : [];
    return Object.values(panels)
        .map((p) => p?.config)
        .filter((cfg) => {
            if (!cfg) return false;
            const t = cfg.chart_type || cfg.type;
            return types.includes(t);
        });
};

export const dashboardFiltersRequestThunk = createAsyncThunk(
    'dashboard/filters',
    async ({ dashboardId }, thunkAPI) => {
        try {
            const res = await dashboardFiltersRequest(thunkAPI, dashboardId);
            return res.data;
        } catch (err) {
            const httpStatusCode = err.response?.status || (err.code === 'ERR_NETWORK' || err.message?.includes('ERR_CONNECTION_REFUSED') ? 503 : 500);
            return thunkAPI.rejectWithValue({ http_status_code: httpStatusCode });
        }
    }
)

export const dashboardDataRequestThunk = createAsyncThunk(
    'dashboard/data',
    async ({ panel, filterConfig, filterValues, extra }, thunkAPI) => {
        try {
            const res = await dashboardDataRequest(panel, thunkAPI, { filterConfig, filterValues, extra });
            thunkAPI.loading = true
            return thunkAPI.fulfillWithValue(res.data)
        } catch (err) {
            return thunkAPI.rejectWithValue(getDashboardRequestError(err))
        }
    },
    {
        condition: ({ dashboardId, panel, filterConfig, filterValues, extra }, { getState }) => {
            const state = getState();
            const effectiveDashboardId = dashboardId || state?.dashboard?.currentDashboardId;
            const panelKey = getPanelKey(panel);
            const panelState = state?.dashboard?.dashboards?.[effectiveDashboardId]?.panels?.[panelKey];
            const requestKey = buildPanelRequestKey({
                dashboardId: effectiveDashboardId,
                panel,
                filterConfig,
                filterValues,
                extra,
            });

            return !(panelState?.loading && panelState?.pendingRequestKey === requestKey);
        },
    }
)

export const dashboardPeriodicDataRequestThunk = createAsyncThunk(
    'dashboard/periodicData',
    async ({ dashboardId, panelTypes, filterConfig, filterValues }, thunkAPI) => {
        try {
            const state = thunkAPI.getState();

            const effectiveDashboardId = dashboardId || state?.dashboard?.currentDashboardId;
            if (!effectiveDashboardId) {
                return thunkAPI.fulfillWithValue({ dashboardId: null, results: {}, errors: {} });
            }

            const effectiveFilterConfig = filterConfig || state?.dashboard?.dashboards?.[effectiveDashboardId]?.filters?.config || [];
            const effectiveFilterValues = filterValues || state?.dashboard?.dashboards?.[effectiveDashboardId]?.filters?.currentValues || {};

            const panelConfigs = selectDashboardPanelsByType(state, effectiveDashboardId, panelTypes);
            const settled = await Promise.allSettled(
                panelConfigs.map(async (panelConfig) => {
                    const res = await dashboardDataRequest(panelConfig, thunkAPI, {
                        filterConfig: effectiveFilterConfig,
                        filterValues: effectiveFilterValues,
                    });
                    return { panelId: panelConfig.id, data: res.data };
                })
            );

            const results = {};
            const errors = {};
            settled.forEach((s, idx) => {
                const panelId = panelConfigs[idx]?.id;
                if (!panelId) return;
                if (s.status === 'fulfilled') {
                    results[panelId] = s.value.data;
                } else {
                    const err = s.reason;
                    errors[panelId] = getDashboardRequestError(err);
                }
            });

            return thunkAPI.fulfillWithValue({ dashboardId: effectiveDashboardId, results, errors });
        } catch (err) {
            return thunkAPI.rejectWithValue(getDashboardRequestError(err));
        }
    }
)

export const startDashboardPeriodicDataRequestCron = createAsyncThunk(
    'dashboard/startPeriodicCron',
    async (
        { dashboardId, panelTypes, intervalMs = 10_000, filterConfig, filterValues },
        thunkAPI
    ) => {
        if (dashboardPeriodicCronIntervalId) {
            clearInterval(dashboardPeriodicCronIntervalId);
            dashboardPeriodicCronIntervalId = null;
        }

        const tick = () => {
            const state = thunkAPI.getState();
            const effectiveFilterConfig = filterConfig || state?.dashboard?.dashboards?.[dashboardId]?.filters?.config || [];
            const effectiveFilterValues = filterValues || state?.dashboard?.dashboards?.[dashboardId]?.filters?.currentValues || {};
            thunkAPI.dispatch(
                dashboardPeriodicDataRequestThunk({
                    dashboardId,
                    panelTypes,
                    filterConfig: effectiveFilterConfig,
                    filterValues: effectiveFilterValues,
                })
            );
        };

        tick();
        dashboardPeriodicCronIntervalId = setInterval(tick, intervalMs);

        return thunkAPI.fulfillWithValue({ started: true });
    }
)

export const stopDashboardPeriodicDataRequestCron = createAsyncThunk(
    'dashboard/stopPeriodicCron',
    async (_, thunkAPI) => {
        if (dashboardPeriodicCronIntervalId) {
            clearInterval(dashboardPeriodicCronIntervalId);
            dashboardPeriodicCronIntervalId = null;
        }
        return thunkAPI.fulfillWithValue({ stopped: true });
    }
)

export const dashboardOptionsRequestThunk = createAsyncThunk(
    'dashboard/panels',
    async (_, thunkAPI) => {
        try {
            const res = await dashboardPanelsRequest(thunkAPI);
            thunkAPI.loadingDashboard = true;
            return thunkAPI.fulfillWithValue(res.data);
        } catch (err) {
            console.error(err);
            return thunkAPI.rejectWithValue({
                http_status_code: err.response?.status
            });
        }
    },
    {
        condition: (arg, { getState }) => {
            if (arg?.force) return true;

            const dashboardState = getState()?.dashboard;
            const hasDashboards = Object.keys(dashboardState?.dashboards || {}).length > 0;

            return !dashboardState?.loading && !hasDashboards;
        },
    }
);

export const dashboardDateFilterRequestThunk = createAsyncThunk(
    'dashboard/date_filter',
    async (opt, thunkAPI) => {
        try {
            const res = await dashboardDateFilterRequest(thunkAPI, opt.data);
            return thunkAPI.fulfillWithValue(res.status)
        } catch (err) {
            console.error(err)
            return thunkAPI.rejectWithValue({
                http_status_code: err.response?.status
            })
        }
    }
)
